/**
 * Charts.
 *
 * The handoff flags the sparkline and bar charts as the one part not final:
 * hand-built divs, to be swapped for a charting library keeping the exact
 * colours and proportions. There is no charting library in this repo and adding
 * one for four small charts is not justified, so they are kept as divs — but
 * isolated here, with the proportions as the only input, so a library swap
 * touches this file alone.
 *
 * Every chart renders nothing rather than a flat baseline when it has no data.
 */
import { c, mono } from '../theme/tokens';
import { Mono } from './primitives';
import type { HourBucket, SentimentDay, VolumeDay } from '../api/types';

/** 7-bar sparkline, 26px tall, 2px radius, 3px gap, #D6E4F1. */
export function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) {
    return <div style={{ height: 26 }} />;
  }
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 26 }} aria-hidden="true">
      {values.map((v, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            borderRadius: 2,
            background: c.indigo100,
            height: `${Math.max(Math.round((v / max) * 100), 4)}%`,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Hour histogram, using the same two categories as the service totals above it.
 */
export function HourChart({ hours, height = 180, compact = false }: { hours: HourBucket[]; height?: number; compact?: boolean }) {
  const peak = Math.max(...hours.map(h => h.total), 1);
  // A 24-hour axis needs fewer labels than the former 10-hour office-hours
  // view, particularly inside the narrow mobile card. Keep the first, each
  // three-hour landmark and the final hour, while every bar remains present.
  const sparseAxis = hours.length > 12;

  return (
    <div className="vp-hour-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: compact ? 8 : sparseAxis ? 'clamp(2px, 1vw, 6px)' : 10, height }}>
      {hours.map(h => {
        const barHeight = h.total === 0 ? 0 : Math.round((h.total / peak) * (height - 46)) + 6;
        const pct = (n: number) => (h.total === 0 ? 0 : Math.round((n / h.total) * 100));
        const showAxisLabel = !sparseAxis || h.hour % 3 === 0 || h.hour === 23;
        const showValue = !compact && (!sparseAxis || h.hour % 2 === 0);
        return (
          <div
            key={h.hour}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: compact ? 8 : 9,
              height: '100%',
              justifyContent: 'flex-end',
            }}
            title={`${h.label}:00 — ${h.total} ${h.total === 1 ? 'call' : 'calls'}`}
          >
            {showValue ? (
              <span style={{ fontFamily: mono, fontSize: 10.5, color: h.total === 0 ? c.ink3 : c.ink }}>
                {h.total === 0 ? '' : h.total}
              </span>
            ) : null}
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: 2,
                height: barHeight,
              }}
            >
              <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: c.indigo, height: `${pct(h.sharedWithBranch)}%` }} />
              <div style={{ width: '100%', borderRadius: '0 0 3px 3px', background: c.lapis, height: `${pct(h.shortAbandoned)}%` }} />
            </div>
            <span style={{ fontFamily: mono, fontSize: compact ? 10 : 10.5, color: c.ink3, visibility: showAxisLabel ? 'visible' : 'hidden' }}>{h.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Daily call volume across the selected range.
 *
 * Deliberately plain: one bar per day, segmented to match the top-line service
 * totals. Value labels are only drawn when the bars are
 * wide enough to hold them, and axis labels thin out on long ranges rather
 * than overlapping into mush.
 */
export function VolumeChart({
  days,
  height = 210,
  onPickDay,
}: {
  days: VolumeDay[];
  height?: number;
  onPickDay?: (date: string) => void;
}) {
  if (days.length === 0) return null;

  const peak = Math.max(...days.map(d => d.total), 1);
  const plot = height - 46;
  // Thin the axis so labels never collide: aim for ~12 labels maximum.
  const labelEvery = Math.max(1, Math.ceil(days.length / 12));
  const showValues = days.length <= 14;
  const total = days.reduce((a, d) => a + d.total, 0);
  const busiest = days.reduce<VolumeDay | null>((best, d) => (d.total > 0 && (!best || d.total > best.total) ? d : best), null);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: days.length > 40 ? 2 : days.length > 20 ? 4 : 7, height }}>
        {days.map((d, i) => {
          const barHeight = d.total === 0 ? 2 : Math.round((d.total / peak) * plot) + 4;
          const pct = (n: number) => (d.total === 0 ? 0 : Math.round((n / d.total) * 100));
          const isBusiest = busiest != null && d.date === busiest.date;
          return (
            <div
              key={d.date}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                height: '100%',
                justifyContent: 'flex-end',
              }}
              title={`${d.label}: ${d.total} ${d.total === 1 ? 'call' : 'calls'}${d.total > 0 ? ` · ${d.sharedWithBranch} conversations shared with branch, ${d.shortAbandoned} short calls abandoned` : ''}`}
            >
              {showValues ? (
                <span style={{ fontFamily: mono, fontSize: 10.5, color: isBusiest ? c.ink : c.ink3 }}>
                  {d.total === 0 ? '' : d.total}
                </span>
              ) : null}
              <button
                className="vp-reset"
                onClick={() => onPickDay?.(d.date)}
                disabled={!onPickDay || d.total === 0}
                aria-label={`${d.label}: ${d.total} calls.${onPickDay && d.total > 0 ? ' View these calls.' : ''}`}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  gap: 2,
                  height: barHeight,
                  cursor: onPickDay && d.total > 0 ? 'pointer' : 'default',
                  // A zero day still shows a hairline, so the reader can see
                  // the day existed and genuinely had no calls.
                  background: d.total === 0 ? c.lineSoft : undefined,
                  borderRadius: d.total === 0 ? 2 : undefined,
                }}
              >
                {d.total > 0 ? (
                  <>
                    <div style={{ width: '100%', borderRadius: '3px 3px 0 0', background: c.indigo, height: `${pct(d.sharedWithBranch)}%` }} />
                    <div style={{ width: '100%', borderRadius: '0 0 3px 3px', background: c.lapis, height: `${pct(d.shortAbandoned)}%` }} />
                  </>
                ) : null}
              </button>
              <span
                style={{
                  fontFamily: mono,
                  fontSize: 10,
                  color: c.ink3,
                  whiteSpace: 'nowrap',
                  visibility: i % labelEvery === 0 || i === days.length - 1 ? 'visible' : 'hidden',
                }}
              >
                {d.label.replace(' ', '\u00A0')}
              </span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 12, fontSize: 12.5, color: c.ink3 }}>
        {total} {total === 1 ? 'call' : 'calls'} across {days.length} {days.length === 1 ? 'day' : 'days'}
        {busiest ? ` · busiest ${busiest.label} with ${busiest.total}` : ''}
      </div>
    </div>
  );
}

export function HourKey() {
  const keys = [
    { name: 'Conversations shared with branch', color: c.indigo },
    { name: 'Short calls abandoned', color: c.lapis },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {keys.map(k => (
        <span key={k.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: k.color }} />
          <span style={{ fontSize: 11.5, color: c.ink3 }}>{k.name}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Stacked sentiment bars, one per day.
 *
 * IMPORTANT — bars are scaled by VOLUME, not just composition. A day with one
 * frustrated call out of two is 50% frustrated; drawn as a full-height bar it
 * screams louder than a 40-call day that was 12% frustrated, and the reader
 * concludes the service is failing. So each day's bar height is proportional to
 * how many calls it actually had, and days below `confidentAt` are drawn muted
 * with the raw count shown. The percentages are unchanged and still exact — we
 * are fixing how much VISUAL WEIGHT a small sample is allowed to claim.
 */
export function SentimentBars({
  days,
  height = 120,
  barHeight = 96,
  gap = 14,
  maxWidth,
  labelSize = 10.5,
  onSegmentClick,
  confidentAt = 5,
  showCounts = false,
}: {
  days: SentimentDay[];
  height?: number;
  barHeight?: number;
  gap?: number;
  maxWidth?: number;
  labelSize?: number;
  onSegmentClick?: (date: string, sentiment: 'positive' | 'neutral' | 'frustrated') => void;
  /** Below this many analysed calls a day is treated as a thin sample. */
  confidentAt?: number;
  showCounts?: boolean;
}) {
  // Scale against the busiest day, with a floor so a quiet week still reads.
  const peak = Math.max(...days.map(d => d.total), 1);
  // Minimum visible height for a day that had at least one analysed call.
  const floor = 0.18;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap, height }}>
      {days.map(d => {
        const thin = d.total > 0 && d.total < confidentAt;
        const scale = d.total === 0 ? 0 : Math.max(floor, d.total / peak);
        const thisBar = Math.round(barHeight * scale);
        const countText = `${d.total} analysed`;
        return (
          <div
            key={d.date}
            style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 9, height: '100%', justifyContent: 'flex-end' }}
            title={
              d.total === 0
                ? `${d.label} — no analysed calls`
                : `${d.label} — ${countText} · ${d.posN} positive, ${d.neuN} neutral, ${d.fruN} frustrated (${d.pos}% / ${d.neu}% / ${d.fru}%)${thin ? ' · too few calls to read as a trend' : ''}`
            }
          >
            {showCounts ? (
              <Mono size={9.5} color={d.total === 0 ? c.ink3 : undefined} style={{ whiteSpace: 'nowrap' }}>
                {d.total === 0 ? '' : d.total}
              </Mono>
            ) : null}
            <div
              style={{
                width: '100%',
                maxWidth,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                height: d.total === 0 ? 2 : thisBar,
                justifyContent: 'flex-end',
                // A day with no analysed calls keeps a hairline so the reader
                // sees the day existed rather than silently losing it.
                background: d.total === 0 ? 'rgba(255,255,255,0.18)' : undefined,
                borderRadius: d.total === 0 ? 2 : undefined,
                // Thin samples are visibly less assertive.
                opacity: thin ? 0.55 : 1,
              }}
            >
              {d.total > 0
                ? (['positive', 'neutral', 'frustrated'] as const).map(kind => {
                    const value = kind === 'positive' ? d.pos : kind === 'neutral' ? d.neu : d.fru;
                    const count = kind === 'positive' ? d.posN : kind === 'neutral' ? d.neuN : d.fruN;
                    const colour = kind === 'positive' ? c.success : kind === 'neutral' ? c.neutralBar : c.danger;
                    const rounded = kind === 'positive' ? '3px 3px 0 0' : kind === 'frustrated' ? '0 0 3px 3px' : undefined;
                    return (
                      <button
                        key={kind}
                        className="vp-reset"
                        onClick={() => onSegmentClick?.(d.date, kind)}
                        disabled={!onSegmentClick || count === 0}
                        style={{
                          width: '100%',
                          background: colour,
                          opacity: kind === 'neutral' ? 1 : 0.85,
                          height: `${value}%`,
                          borderRadius: rounded,
                          cursor: onSegmentClick && count > 0 ? 'pointer' : 'default',
                        }}
                        aria-label={`${d.label}: ${count} of ${d.total} ${kind}. View calls.`}
                      />
                    );
                  })
                : null}
            </div>
            <Mono size={labelSize} style={{ whiteSpace: 'nowrap', transform: 'rotate(-48deg)', transformOrigin: 'top center', marginTop: 8 }}>{d.label}</Mono>
          </div>
        );
      })}
    </div>
  );
}

/** Theme volume track — 6px or 8px, indigo fill, orange when selected. */
export function ThemeTrack({
  pct,
  height = 6,
  fill = c.indigo,
  width,
}: {
  pct: number;
  height?: number;
  fill?: string;
  width?: number;
}) {
  return (
    <div
      style={{
        flex: width ? `0 0 ${width}px` : undefined,
        width: width ?? '100%',
        height,
        background: c.lineSoft,
        borderRadius: 9999,
        overflow: 'hidden',
      }}
    >
      <div style={{ height: '100%', borderRadius: 9999, background: fill, width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
    </div>
  );
}

/** Movement figure: green up, orange down, grey flat. */
export function Movement({ movement, size = 11, width = 34 }: { movement: number | null; size?: number; width?: number }) {
  if (movement == null) {
    return <Mono size={size} style={{ width, textAlign: 'right' }}>—</Mono>;
  }
  const colour = movement > 0 ? c.success : movement < 0 ? c.orange700 : c.ink3;
  const text = movement > 0 ? `+${movement}` : movement < 0 ? `−${Math.abs(movement)}` : '0';
  return (
    <Mono size={size} color={colour} style={{ width, textAlign: 'right' }}>
      {text}
    </Mono>
  );
}
